import { DragEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setUserParam,
  setError,
  setSolutionJson,
  setSolutionParam,
  setTourParameter,
} from 'actions';
import { useCookies } from 'react-cookie';
import { PANELS } from 'reducers/user';
import {
  getLastValidIteration,
  getTotalJobsInTour,
  getTotalJobsInTours,
  getToursDataFromIteration,
} from 'utils/SolutionHelpers';
import { formatPriceWithCurrency } from 'utils/formatter';
import { isMobileDevice, sprintf } from 'utils/helpers';
import { uploadCsvToAiWorkspace } from 'utils/aiChatUploadHelpers';
import { fetchAiWorkspaceArtifact } from 'utils/aiChatArtifactHelpers';
import {
  getAiProblemIdempotencyKey,
  reconcileAiWorkspaceProblem,
} from 'utils/aiChatProblemReconciliationHelpers';
import { advanceAiWorkspaceTask } from 'utils/aiChatTaskHelpers';
import { streamChatMessage, SSE_EVENT_TYPES } from 'utils/aiChatStreamHelpers';
import { createAiWorkspace } from 'utils/aiChatWorkspaceHelpers';
import { generateOrdersFromFileJSON, integrateFileText } from 'utils/apiFilesUploadHelpers';
import {
  PROBLEM_JSON_ERROR_CODES,
  validateProblemJson,
} from 'utils/aiChatProblemHelpers';
import {
  isAcceptedFileType,
  getUploadErrorBody,
  formatDurationLabel,
  getTourLabel,
  formatMinutesLabel,
  findTightestStop,
  getRandomResponseVariant,
  getVariantLabel,
  getPromptTitle,
  getResponseCard,
} from 'utils/routingAiChatHelpers';
import {
  THREAD_ITEM_TYPES,
  RESPONSE_VARIANTS,
  TIGHT_BUFFER_THRESHOLD_MINUTES,
  MAX_UPLOAD_FILE_SIZE_BYTES,
  type RoutingAiChatTranslation,
  type UnitsTranslation,
  type PromptKey,
  type CardAction,
  type ResponseCardData,
  type ThreadItem,
  type ResponseThreadItem,
  type TraceStep,
  type Summary,
  type SolutionState,
  type UserState,
  type RootState,
  type TourData,
  type ArtifactInfo,
  type ArtifactInfoDataResponse,
  type ArtifactRef,
  type GeocodingTask,
} from 'components/Chat/RoutingAIChatTypes';
import axios from 'axios';

const useRoutingAIChat = ({
  routingAiChat,
  units,
  error,
  map,
  onOpenExternalJsonSession,
  onCloseExternalJsonSession,
}: {
  routingAiChat: RoutingAiChatTranslation;
  units: UnitsTranslation;
  error: Record<string, string>;
  map: { depotMarkerTrans: string; returnLocationMarkerTrans: string };
  onOpenExternalJsonSession?: Function;
  onCloseExternalJsonSession?: Function;
}) => {
  const dispatch = useDispatch();
  const solution = useSelector<RootState, SolutionState>(
    ({ solution: stateSolution }) => stateSolution,
  );
  const user = useSelector<RootState, UserState>(({ user: stateUser }) => stateUser);
  const oAuth = useSelector<RootState, unknown>(({ oAuth: stateOAuth }) => stateOAuth);
  const [cookies] = useCookies(['apikey']);

  const [showPanel, setShowPanel] = useState(false);
  const [draft, setDraft] = useState('');
  const [threadItems, setThreadItems] = useState<ThreadItem[]>([]);
  const [selectedActionsByMessage, setSelectedActionsByMessage] = useState<
    Record<string, CardAction['key']>
  >({});
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isWorkspaceConnecting, setIsWorkspaceConnecting] = useState(false);
  const [generatedProblemJson, setGeneratedProblemJson] = useState<string | null>(null);
  const [generatedProblemOriginalJson, setGeneratedProblemOriginalJson] = useState<string | null>(null);
  const [generatedProblemArtifactRef, setGeneratedProblemArtifactRef] = useState<ArtifactRef | null>(null);
  const [generatedProblemMessageId, setGeneratedProblemMessageId] = useState<number | string | null>(null);
  const [generatedProblemValidationError, setGeneratedProblemValidationError] = useState<string | null>(null);
  const [generatedProblemMode, setGeneratedProblemMode] = useState<'edit' | 'view' | null>(null);
  const [submittedProblemJsonBySource, setSubmittedProblemJsonBySource] = useState<
    Record<string, { originalJson: string; submittedJson: string }>
  >({});
  const [generatedProblemCopied, setGeneratedProblemCopied] = useState(false);

  const nextMessageId = useRef<number>(0);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamingCardIdRef = useRef<number | null>(null);
  const streamingPendingReset = useRef(false);
  const nextTraceIdRef = useRef<number>(0);
  const geocodingPollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const geocodingBackoffMsRef = useRef(2000);
  const geocodingContinuationTaskIdsRef = useRef(new Set<string>());
  const geocodingTraceCardIdsRef = useRef(new Map<string, number | string>());
  const canvasBuildContinuationVersionsRef = useRef(new Set<number>());

  const stopGeocodingPolling = useCallback(() => {
    if (geocodingPollTimerRef.current !== null) {
      clearTimeout(geocodingPollTimerRef.current);
      geocodingPollTimerRef.current = null;
    }
    geocodingBackoffMsRef.current = 2000;
  }, []);

  const handleCloseGeneratedProblemJson = useCallback(() => {
    setGeneratedProblemJson(null);
    setGeneratedProblemOriginalJson(null);
    setGeneratedProblemArtifactRef(null);
    setGeneratedProblemMessageId(null);
    setGeneratedProblemValidationError(null);
    setGeneratedProblemMode(null);
    setGeneratedProblemCopied(false);
  }, []);

  const handleCopyGeneratedProblemJson = useCallback(async () => {
    if (!generatedProblemJson) return;
    await navigator.clipboard.writeText(generatedProblemJson);
    setGeneratedProblemCopied(true);
  }, [generatedProblemJson]);

  const getGeneratedProblemValidationMessage = useCallback(
    (errorCode: string | null) => {
      const messages = {
        [PROBLEM_JSON_ERROR_CODES.INVALID_JSON]: routingAiChat.generatedProblemInvalidJson,
        [PROBLEM_JSON_ERROR_CODES.EMPTY_JSON]: routingAiChat.generatedProblemEmptyJson,
        [PROBLEM_JSON_ERROR_CODES.NO_PLAN]: routingAiChat.generatedProblemNoPlan,
        [PROBLEM_JSON_ERROR_CODES.NO_JOBS]: routingAiChat.generatedProblemNoJobs,
        [PROBLEM_JSON_ERROR_CODES.NO_FLEET]: routingAiChat.generatedProblemNoFleet,
      };
      return errorCode ? messages[errorCode as keyof typeof messages] : null;
    },
    [routingAiChat],
  );

  const handleGeneratedProblemJsonChange = useCallback(
    (value: string) => {
      setGeneratedProblemJson(value);
      setGeneratedProblemValidationError(
        getGeneratedProblemValidationMessage(validateProblemJson(value).errorCode),
      );
    },
    [getGeneratedProblemValidationMessage],
  );

  const getGeneratedProblemJsons = useCallback(
    async (ref: ArtifactRef) => {
      const sourceKey = `${user.aiChatWorkspaceId}:${ref.artifact}:${ref.version}`;
      const submittedProblem = submittedProblemJsonBySource[sourceKey];
      if (submittedProblem) return submittedProblem;

      const artifact = await fetchAiWorkspaceArtifact(
        user.aiChatWorkspaceId!,
        ref.artifact,
        ref.version,
      );
      const originalJson = JSON.stringify(artifact.content, null, 2);
      return { originalJson, submittedJson: originalJson };
    },
    [submittedProblemJsonBySource, user.aiChatWorkspaceId],
  );

  const applyGeneratedProblemJson = useCallback(async ({
    ref,
    originalJson,
    json,
    messageId,
  }: {
    ref: ArtifactRef;
    originalJson: string;
    json: string;
    messageId: number | string;
  }) => {
    const validation = validateProblemJson(json);
    const validationMessage = getGeneratedProblemValidationMessage(validation.errorCode);
    if (validationMessage || !validation.json) {
      setGeneratedProblemValidationError(validationMessage);
      return false;
    }

    if (generateOrdersFromFileJSON(validation.json).length === 0) {
      setGeneratedProblemValidationError(error.invalidFileFormat);
      return false;
    }

    const filename = `${ref.artifact}-v${ref.version}-submitted.json`;
    const sourceKey = `${user.aiChatWorkspaceId}:${ref.artifact}:${ref.version}`;
    setSubmittedProblemJsonBySource((current) => ({
      ...current,
      [sourceKey]: {
        originalJson,
        submittedJson: JSON.stringify(validation.json, null, 2),
      },
    }));
    setSelectedActionsByMessage((current) => ({
      ...current,
      [String(messageId)]: 'useGeneratedProblem',
    }));
    dispatch(setTourParameter({ ordersMode: 'json_import', index: solution.show }));
    dispatch(setSolutionParam({ param: { name: filename }, index: solution.show }));
    await integrateFileText(
      JSON.stringify(validation.json),
      'upload',
      filename,
      user,
      cookies,
      oAuth,
      dispatch,
      true,
      error,
      solution.show,
      false,
      true,
      false,
      map.depotMarkerTrans,
      map.returnLocationMarkerTrans,
      undefined,
      'minimumSpan',
    );
    dispatch(
      setSolutionJson({
        json: true,
        jsonPending: false,
        jsonSource: 'aiGenerated',
        jsonUserChange: false,
      }),
    );
    setThreadItems((current) =>
      current.map((item): ThreadItem =>
        item.id === messageId
          ? {
              ...(item as ResponseThreadItem),
              body: `${(item as ResponseThreadItem).body}\n\n${sprintf(
                routingAiChat.generatedProblemApplied,
                {
                  filename,
                  original: `${ref.artifact}-v${ref.version}.json`,
                },
              )}`,
              variant: RESPONSE_VARIANTS.READY,
            }
          : item,
      ),
    );
    return true;
  }, [
    cookies,
    dispatch,
    error,
    getGeneratedProblemValidationMessage,
    map.depotMarkerTrans,
    map.returnLocationMarkerTrans,
    oAuth,
    routingAiChat.generatedProblemApplied,
    solution.show,
    user,
  ]);

  const handleApplyGeneratedProblemJson = useCallback(async () => {
    if (!generatedProblemJson || !generatedProblemArtifactRef || generatedProblemMessageId === null) return;

    const applied = await applyGeneratedProblemJson({
      ref: generatedProblemArtifactRef,
      originalJson: generatedProblemOriginalJson || generatedProblemJson,
      json: generatedProblemJson,
      messageId: generatedProblemMessageId,
    });
    if (applied) handleCloseGeneratedProblemJson();
  }, [
    applyGeneratedProblemJson,
    generatedProblemArtifactRef,
    generatedProblemJson,
    generatedProblemMessageId,
    generatedProblemOriginalJson,
    handleCloseGeneratedProblemJson,
  ]);

  const openGeneratedProblemJson = useCallback(
    async ({ ref, messageId, mode }: { ref: ArtifactRef; messageId: number | string; mode: 'edit' | 'view' }) => {
      const { originalJson, submittedJson } = await getGeneratedProblemJsons(ref);
      if (onOpenExternalJsonSession) {
        onOpenExternalJsonSession({
          mode,
          sourceProblemRef: ref,
          sourceJson: originalJson,
          draftJson: JSON.parse(submittedJson),
          onApply: async (draftJson: Record<string, unknown>) => {
            if (mode !== 'edit') return false;
            try {
              const reconciliation = await reconcileAiWorkspaceProblem({
                workspaceId: user.aiChatWorkspaceId,
                sourceProblem: ref,
                editedProblem: draftJson,
                idempotencyKey: getAiProblemIdempotencyKey({
                  workspaceId: user.aiChatWorkspaceId,
                  sourceProblem: ref,
                  editedProblem: draftJson,
                }),
              });
              if (!reconciliation?.problemRef) return false;
              if (reconciliation.status === 'no_change') {
                setThreadItems((current) =>
                  current.map((item): ThreadItem =>
                    item.id === messageId
                      ? {
                          ...(item as ResponseThreadItem),
                          body: `${(item as ResponseThreadItem).body}\n\n${routingAiChat.generatedProblemNoChanges}`,
                          variant: RESPONSE_VARIANTS.READY,
                        }
                      : item,
                  ),
                );
                return true;
              }
              const reconciledRef = reconciliation?.problemRef;
              const artifact = await fetchAiWorkspaceArtifact(
                user.aiChatWorkspaceId,
                reconciledRef.artifact,
                reconciledRef.version,
              );
              const reconciledJson = JSON.stringify(artifact.content, null, 2);
              const applied = await applyGeneratedProblemJson({
                ref: reconciledRef,
                originalJson: reconciledJson,
                json: reconciledJson,
                messageId,
              });
              if (applied) {
                const workspaceKey = user.aiChatWorkspaceId;
                setSubmittedProblemJsonBySource((current) => ({
                  ...current,
                  [`${workspaceKey}:${reconciledRef.artifact}:${reconciledRef.version}`]: {
                    originalJson: reconciledJson,
                    submittedJson: reconciledJson,
                  },
                }));
                setThreadItems((current) =>
                  current.map((item): ThreadItem => {
                    if (item.id !== messageId) return item;
                    const response = item as ResponseThreadItem;
                    return {
                      ...response,
                      problemArtifactRef: reconciledRef,
                      actions: response.actions.map((candidate) => ({
                        ...candidate,
                        artifactRef: reconciledRef,
                      })),
                    };
                  }),
                );
              }
              return applied;
            } catch {
              dispatch(setError(routingAiChat.generatedProblemLoadError));
              return false;
            }
          },
        });
        return;
      }
      setGeneratedProblemOriginalJson(originalJson);
      setGeneratedProblemJson(submittedJson);
      setGeneratedProblemArtifactRef(ref);
      setGeneratedProblemMessageId(messageId);
      setGeneratedProblemMode(mode);
      setGeneratedProblemValidationError(
        mode === 'edit'
          ? getGeneratedProblemValidationMessage(validateProblemJson(submittedJson).errorCode)
          : null,
      );
      setGeneratedProblemCopied(false);
    },
    [
      applyGeneratedProblemJson,
      getGeneratedProblemJsons,
      getGeneratedProblemValidationMessage,
      onOpenExternalJsonSession,
      user.aiChatWorkspaceId,
    ],
  );

  const formatFileSize = useCallback((bytes: number) => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';

    const sizeUnits = ['B', 'KB', 'MB', 'GB'];
    const exponent = Math.min(
      Math.floor(Math.log(bytes) / Math.log(1024)),
      sizeUnits.length - 1,
    );
    const value = bytes / 1024 ** exponent;
    const rounded = exponent === 0 ? value.toFixed(0) : value.toFixed(1);

    return `${rounded} ${sizeUnits[exponent]}`;
  }, []);

  const getUploadPromptText = useCallback(
    (file: File) =>
      sprintf(routingAiChat.uploadCsv.uploadedFileTemplate, {
        fileName: file.name,
        fileSize: formatFileSize(file.size),
      }),
    [formatFileSize, routingAiChat.uploadCsv.uploadedFileTemplate],
  );

  const summary = useMemo<Summary>(() => {
    const request = solution.requests?.[solution.show];
    const iteration = getLastValidIteration(request);
    const tourData = getToursDataFromIteration(iteration) as TourData | undefined;

    if (!tourData?.tours?.length) {
      return {
        hasPlan: false,
        key: `no-plan-${solution.show}`,
      };
    }

    const tours = tourData.tours || [];
    const assignedOrders = getTotalJobsInTours(tours) || 0;
    const unassignedOrders = tourData.unassigned?.length || 0;
    const totalOrders = assignedOrders + unassignedOrders;
    const totalCost = formatPriceWithCurrency(
      tourData.statistic?.cost || 0,
      user.currency || 'EUR',
    );
    const totalDuration = formatDurationLabel(tourData.statistic?.duration, units);
    const tightestStop = findTightestStop(tours, tourData.plan?.jobs);
    const maxOrders = Math.max(...tours.map((tour) => getTotalJobsInTour(tour.stops) || 0));
    const avgOrders = tours.length > 0 ? (assignedOrders / tours.length).toFixed(1) : '0.0';
    const rebalancedOrders =
      tours.length > 0 ? (assignedOrders / (tours.length + 1)).toFixed(1) : '0.0';

    return {
      hasPlan: true,
      key: `${solution.show}-${assignedOrders}-${totalOrders}-${tourData.statistic?.cost || 0}`,
      assignedOrders,
      totalOrders,
      totalCost,
      totalDuration,
      tightestStop,
      tourCount: tours.length,
      tourLabel: getTourLabel(tours.length, routingAiChat),
      avgOrders,
      maxOrders,
      rebalancedOrders,
    };
  }, [routingAiChat, solution.requests, solution.show, units, user.currency]);

  const summaryText = useMemo(() => {
    if (!summary.hasPlan) return '';

    return sprintf(routingAiChat.summaryTemplate, {
      tourCount: summary.tourCount,
      tourLabel: summary.tourLabel,
      assignedOrders: summary.assignedOrders,
      totalOrders: summary.totalOrders,
      totalCost: summary.totalCost,
      totalDuration: summary.totalDuration,
    });
  }, [routingAiChat.summaryTemplate, summary]);

  const insightText = useMemo(() => {
    if (!summary.hasPlan) return '';
    if (
      !summary.tightestStop ||
      summary.tightestStop.slackMinutes > TIGHT_BUFFER_THRESHOLD_MINUTES
    ) {
      return routingAiChat.noInsight;
    }

    return sprintf(routingAiChat.insightTemplate, {
      stopNumber: summary.tightestStop.stopNumber,
      tourNumber: summary.tightestStop.tourNumber,
      buffer: formatMinutesLabel(summary.tightestStop.slackMinutes, units),
    });
  }, [routingAiChat, summary, units]);

  const statusMessage = useMemo(() => {
    if (!summary.hasPlan) return routingAiChat.noPlanYet;
    return `${summaryText} ${insightText}`.trim();
  }, [insightText, routingAiChat.noPlanYet, summary.hasPlan, summaryText]);

  const welcomeCard = useMemo<ResponseThreadItem>(
    () => ({
      id: 'welcome-ready-card',
      type: THREAD_ITEM_TYPES.RESPONSE,
      variant: RESPONSE_VARIANTS.READY,
      label: routingAiChat.readyLabel,
      body: statusMessage,
      actions: [],
    }),
    [routingAiChat.readyLabel, statusMessage],
  );

  const syncPanelState = useCallback(
    (nextState: boolean) => {
      dispatch(
        setUserParam({
          openPanel: nextState ? PANELS.ROUTING_AI_CHAT : undefined,
        }),
      );
      setShowPanel(nextState);
    },
    [dispatch],
  );

  const appendWorkspaceError = useCallback(
    (body: string) => {
      setThreadItems((current) => [
        ...current,
        {
          id: nextMessageId.current++,
          type: THREAD_ITEM_TYPES.RESPONSE,
          variant: RESPONSE_VARIANTS.WARNING,
          label: routingAiChat.cardLabels.headsUp,
          body,
          actions: [{ key: 'retryConnection', label: routingAiChat.cardActions.retryConnection }],
        },
      ]);
    },
    [routingAiChat],
  );

  const resetConversation = useCallback(() => {
    setThreadItems([]);
    setSelectedActionsByMessage({});
    setDraft('');
    nextMessageId.current = 0;
  }, []);

  const getWorkspaceErrorBody = useCallback(
    () => `${routingAiChat.workspaceSessionUnavailable} ${routingAiChat.workspaceRetryHint}`,
    [routingAiChat.workspaceRetryHint, routingAiChat.workspaceSessionUnavailable],
  );

  const initWorkspace = useCallback(async () => {
    setIsWorkspaceConnecting(true);
    try {
      const workspaceId = await createAiWorkspace();
      dispatch(setUserParam({ aiChatWorkspaceId: workspaceId }));
      return true;
    } catch {
      const errorBody = getWorkspaceErrorBody();
      appendWorkspaceError(errorBody);
      dispatch(setError(errorBody));
      return false;
    } finally {
      setIsWorkspaceConnecting(false);
    }
  }, [appendWorkspaceError, dispatch, getWorkspaceErrorBody]);

  const handleClose = useCallback(() => {
    stopGeocodingPolling();
    syncPanelState(false);
  }, [stopGeocodingPolling, syncPanelState]);

  const handleRestart = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    stopGeocodingPolling();
    resetConversation();
    setSubmittedProblemJsonBySource({});
    onCloseExternalJsonSession?.();
    dispatch(setUserParam({ aiChatWorkspaceId: null }));
    await initWorkspace();
  }, [resetConversation, dispatch, initWorkspace, stopGeocodingPolling, onCloseExternalJsonSession]);

  const appendFlow = useCallback(
    (promptKey: PromptKey, promptLabel: string) => {
      const title = getPromptTitle(promptKey, promptLabel, routingAiChat, summary);
      const response = getResponseCard({
        promptKey,
        routingAiChat,
        summary,
        units,
      }) as ResponseCardData;
      const randomVariant = getRandomResponseVariant();
      const randomizedResponse: ResponseCardData = {
        ...response,
        variant: randomVariant,
        label: getVariantLabel(randomVariant, routingAiChat),
      };

      setThreadItems((current) => [
        ...current,
        {
          id: nextMessageId.current++,
          type: THREAD_ITEM_TYPES.PROMPT,
          text: title,
        },
        {
          id: nextMessageId.current++,
          type: THREAD_ITEM_TYPES.RESPONSE,
          ...randomizedResponse,
        },
      ]);
    },
    [routingAiChat, summary, units],
  );

  const updateGeocodingTaskTrace = useCallback(
    (task: GeocodingTask, initial = false) => {
      const cardId = geocodingTraceCardIdsRef.current.get(task.taskId);
      if (cardId === undefined) return;
      const total = task.requested || task.distinct;
      const terminal = ['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(
        task.state,
      );
      const failed = ['failed', 'cancelled'].includes(task.state);
      const taskStatus = failed ? 'failed' : terminal ? 'completed' : 'running';
      const label = failed
        ? `${routingAiChat.geocodingBatchFailed || 'Batch geocoding failed.'}${task.error ? ` ${task.error}` : ''}`
        : terminal
        ? sprintf(
            routingAiChat.geocodingBatchCompleted || 'Batch geocoding completed for %(resolved)/%(total) addresses.',
            { resolved: task.resolved, total },
          )
        : initial
        ? sprintf(routingAiChat.geocodingBatchStarted || 'Resolving %(total) addresses with HERE.', { total })
        : sprintf(
            routingAiChat.geocodingBatchProgress || 'Resolving %(resolved)/%(total) addresses with HERE.',
            { resolved: task.resolved, total },
          );
      setThreadItems((current) => current.map((item): ThreadItem => {
        if (item.id !== cardId) return item;
        const response = item as ResponseThreadItem;
        const existing = response.traceSteps?.find((step) => step.taskId === task.taskId);
        const step: TraceStep = {
          id: existing?.id ?? nextTraceIdRef.current++,
          kind: 'task',
          label,
          taskId: task.taskId,
          taskMode: task.mode as 'sync' | 'batch',
          taskStatus: taskStatus as 'submitted' | 'running' | 'completed' | 'failed',
          taskCounts: {
            requested: task.requested || task.distinct,
            resolved: task.resolved,
            failed: task.failed,
            pending: task.pending,
          },
        };
        const traceSteps = existing
          ? (response.traceSteps ?? []).map((entry) => entry.id === existing.id ? step : entry)
          : [...(response.traceSteps ?? []), step];
        return { ...response, traceSteps };
      }));
    },
    [routingAiChat],
  );

  const resumeAfterGeocoding = useCallback(
    async (task: GeocodingTask) => {
      if (!user.aiChatWorkspaceId || geocodingContinuationTaskIdsRef.current.has(task.taskId)) return;
      geocodingContinuationTaskIdsRef.current.add(task.taskId);
      const continuationCardId = `geocoding-continuation-${task.taskId}`;
      setThreadItems((current) =>
        [
          ...current,
          {
            id: continuationCardId,
            type: THREAD_ITEM_TYPES.RESPONSE,
            variant: RESPONSE_VARIANTS.ROUTING,
            label: routingAiChat.cardLabels.routingAi,
            body: '',
            actions: [],
            streaming: true,
            traceSteps: [],
          },
        ],
      );

      const updateContinuationCard = (updates: Partial<ResponseThreadItem>) => {
        setThreadItems((current) =>
          current.map((item): ThreadItem =>
            item.id === continuationCardId
              ? ({ ...(item as ResponseThreadItem), ...updates } as ResponseThreadItem)
              : item,
          ),
        );
      };
      const appendContinuationTrace = (step: Omit<TraceStep, 'id'>) => {
        setThreadItems((current) =>
          current.map((item): ThreadItem => {
            if (item.id !== continuationCardId) return item;
            const response = item as ResponseThreadItem;
            return {
              ...response,
              traceSteps: [...(response.traceSteps ?? []), {
                ...step,
                id: nextTraceIdRef.current++,
              }],
            };
          }),
        );
      };

      try {
        let problemBuilt = false;
        await streamChatMessage({
          message: (
            `Geocoding task ${task.taskId} has completed. Continue building the problem from `
            + 'the workspace without asking the user to send a continuation message.'
          ),
          workspaceId: user.aiChatWorkspaceId,
          signal: null,
          onEvent: ({ type, payload }: { type: string; payload: any }) => {
            const agentPath: string[] = payload?.data?.agent_path ?? [];
            const isSubAgent = agentPath.length > 1;
            const tier: string = payload?.tier ?? '';
            if (type === SSE_EVENT_TYPES.MESSAGE_CHUNK) {
              if (isSubAgent) return;
              const delta: string = payload?.data?.text_delta ?? '';
              setThreadItems((current) =>
                current.map((item): ThreadItem =>
                  item.id === continuationCardId
                    ? {
                        ...(item as ResponseThreadItem),
                        body: (item as ResponseThreadItem).body + delta,
                      }
                    : item,
                ),
              );
            } else if (type === SSE_EVENT_TYPES.MESSAGE_COMPLETE) {
              const fullText: string = payload?.data?.full_text ?? '';
              if (isSubAgent) {
                appendContinuationTrace({
                  kind: 'thought',
                  label: fullText.length > 60 ? `${fullText.slice(0, 60)}…` : fullText,
                  detail: fullText.length > 60 ? fullText : undefined,
                });
              } else {
                updateContinuationCard({ body: fullText });
              }
            } else if (type === SSE_EVENT_TYPES.SUBAGENT_INVOKED) {
              appendContinuationTrace({
                kind: 'action',
                label: `Dispatching ${payload?.data?.subagent_name ?? 'assistant'}`,
              });
            } else if (type === SSE_EVENT_TYPES.TOOL_CALLED) {
              appendContinuationTrace({
                kind: 'tool_call',
                label: `${payload?.data?.tool_name ?? 'tool'}()`,
              });
            } else if (tier === 'operational' && type.endsWith('.completed')) {
              appendContinuationTrace({
                kind: 'observation',
                label: payload?.data?.summary ?? '',
              });
            }
            if (
              type === SSE_EVENT_TYPES.BUILD_SCHEMA_COMPLETED &&
              payload?.data?.status === 'success' &&
              payload?.data?.output_artifact === 'problem' &&
              Number.isInteger(payload?.data?.output_version)
            ) {
              problemBuilt = true;
              setSelectedActionsByMessage((current) => {
                const { [String(continuationCardId)]: _selected, ...rest } = current;
                return rest;
              });
              updateContinuationCard({
                actions: [
                  {
                    key: 'useGeneratedProblem',
                    label: routingAiChat.cardActions.useGeneratedProblem,
                    artifactRef: { artifact: 'problem', version: payload.data.output_version },
                  },
                  {
                    key: 'editGeneratedProblem',
                    label: routingAiChat.cardActions.editGeneratedProblem,
                    secondary: true,
                    artifactRef: { artifact: 'problem', version: payload.data.output_version },
                  },
                ],
                problemArtifactRef: { artifact: 'problem', version: payload.data.output_version },
              });
            }
          },
        });
        updateContinuationCard({
          streaming: false,
          variant: RESPONSE_VARIANTS.READY,
          label: routingAiChat.cardLabels.ready,
        });
        if (!problemBuilt) {
          setThreadItems((current) =>
            current.map((item): ThreadItem =>
              item.id === continuationCardId
                ? {
                    ...(item as ResponseThreadItem),
                    body: routingAiChat.geocodingBatchNeedsInput,
                    taskStatus: 'completed',
                  }
                : item,
            ),
          );
        }
      } catch {
        updateContinuationCard({
          streaming: false,
          variant: RESPONSE_VARIANTS.WARNING,
          label: routingAiChat.cardLabels.headsUp,
          body: routingAiChat.chatStreamError,
        });
        setThreadItems((current) =>
          current.map((item): ThreadItem =>
            item.id === continuationCardId
              ? {
                  ...(item as ResponseThreadItem),
                  body: routingAiChat.geocodingBatchNeedsInput,
                  taskStatus: 'completed',
                }
              : item,
          ),
        );
      }
    },
    [routingAiChat, updateGeocodingTaskTrace, user.aiChatWorkspaceId],
  );

  const resumeAfterCanvasUpdate = useCallback(
    async (canvasVersion: number) => {
      if (
        !user.aiChatWorkspaceId ||
        canvasBuildContinuationVersionsRef.current.has(canvasVersion)
      )
        return;

      canvasBuildContinuationVersionsRef.current.add(canvasVersion);
      const continuationCardId = `canvas-build-continuation-${canvasVersion}`;
      setThreadItems((current) => [
        ...current,
        {
          id: continuationCardId,
          type: THREAD_ITEM_TYPES.RESPONSE,
          variant: RESPONSE_VARIANTS.ROUTING,
          label: routingAiChat.cardLabels.routingAi,
          body: '',
          actions: [],
          streaming: true,
          traceSteps: [],
        },
      ]);

      const updateContinuationCard = (updates: Partial<ResponseThreadItem>) => {
        setThreadItems((current) =>
          current.map((item): ThreadItem =>
            item.id === continuationCardId
              ? ({ ...(item as ResponseThreadItem), ...updates } as ResponseThreadItem)
              : item,
          ),
        );
      };

      try {
        await streamChatMessage({
          message: (
            `Canvas version ${canvasVersion} is ready after a user-requested update. `
            + 'Build the current problem artifact now and do not ask the user to send another message.'
          ),
          workspaceId: user.aiChatWorkspaceId,
          signal: null,
          onEvent: ({ type, payload }: { type: string; payload: any }) => {
            if (type === SSE_EVENT_TYPES.MESSAGE_CHUNK) {
              const delta: string = payload?.data?.text_delta ?? '';
              setThreadItems((current) =>
                current.map((item): ThreadItem =>
                  item.id === continuationCardId
                    ? {
                        ...(item as ResponseThreadItem),
                        body: (item as ResponseThreadItem).body + delta,
                      }
                    : item,
                ),
              );
              return;
            }
            if (type === SSE_EVENT_TYPES.MESSAGE_COMPLETE) {
              updateContinuationCard({ body: payload?.data?.full_text ?? '' });
              return;
            }
            if (
              type === SSE_EVENT_TYPES.BUILD_SCHEMA_COMPLETED &&
              payload?.data?.status === 'success' &&
              payload?.data?.output_artifact === 'problem' &&
              Number.isInteger(payload?.data?.output_version)
            ) {
              const ref = { artifact: 'problem', version: payload.data.output_version };
              updateContinuationCard({
                actions: [
                  {
                    key: 'useGeneratedProblem',
                    label: routingAiChat.cardActions.useGeneratedProblem,
                    artifactRef: ref,
                  },
                  {
                    key: 'editGeneratedProblem',
                    label: routingAiChat.cardActions.editGeneratedProblem,
                    secondary: true,
                    artifactRef: ref,
                  },
                ],
                problemArtifactRef: ref,
              });
            }
          },
        });
        updateContinuationCard({
          streaming: false,
          variant: RESPONSE_VARIANTS.READY,
          label: routingAiChat.cardLabels.ready,
        });
      } catch {
        updateContinuationCard({
          streaming: false,
          variant: RESPONSE_VARIANTS.WARNING,
          label: routingAiChat.cardLabels.headsUp,
          body: routingAiChat.chatStreamError,
        });
      }
    },
    [routingAiChat, user.aiChatWorkspaceId],
  );

  const pollGeocodingTask = useCallback(
    async (taskId: string) => {
      try {
        const task = await advanceAiWorkspaceTask(taskId) as GeocodingTask;
        geocodingBackoffMsRef.current = 2000;
        updateGeocodingTaskTrace(task);
        if (['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(task.state)) {
          stopGeocodingPolling();
          if (['completed', 'completed_with_errors'].includes(task.state)) {
            resumeAfterGeocoding(task);
          }
          return;
        }
        const nextPollAt = task.nextPollAt ? Date.parse(task.nextPollAt) : NaN;
        const delay = Number.isNaN(nextPollAt) ? 6000 : Math.max(1000, nextPollAt - Date.now() + 250);
        geocodingPollTimerRef.current = setTimeout(() => {
          pollGeocodingTask(taskId);
        }, delay);
      } catch {
        const delay = geocodingBackoffMsRef.current;
        geocodingBackoffMsRef.current = Math.min(delay * 2, 30000);
        geocodingPollTimerRef.current = setTimeout(() => {
          pollGeocodingTask(taskId);
        }, delay);
      }
  },
    [resumeAfterGeocoding, stopGeocodingPolling, updateGeocodingTaskTrace],
  );

  const startGeocodingTask = useCallback(
    (data: { task_id?: string; mode?: string; state?: string; counts?: Record<string, number> }, cardId: number | string) => {
      if (!data.task_id || !data.mode) return;
      geocodingTraceCardIdsRef.current.set(data.task_id, cardId);
      stopGeocodingPolling();
      const counts = data.counts ?? {};
      const task: GeocodingTask = {
        taskId: data.task_id,
        state: data.state ?? 'running',
        mode: data.mode,
        requested: counts.requested ?? 0,
        distinct: counts.distinct ?? 0,
        resolved: counts.resolved ?? 0,
        failed: counts.failed ?? 0,
        pending: counts.pending ?? counts.requested ?? 0,
      };
      updateGeocodingTaskTrace(task, true);
      if (data.mode === 'batch') {
        geocodingPollTimerRef.current = setTimeout(() => {
          pollGeocodingTask(task.taskId);
        }, 6000);
      }
    },
    [pollGeocodingTask, stopGeocodingPolling, updateGeocodingTaskTrace],
  );

  const handleCardAction = useCallback(
    async (messageId: number | string, action: CardAction) => {
      if (action.key === 'retryConnection') {
        const connected = await initWorkspace();
        if (connected) {
          setThreadItems((current) =>
            current.filter(
              (item) =>
                item.type !== THREAD_ITEM_TYPES.RESPONSE ||
                !item.actions?.some(({ key }) => key === 'retryConnection'),
            ),
          );
        }
        return;
      }

      if (action.key === 'useGeneratedProblem') {
        const ref = action.artifactRef;
        if (!user.aiChatWorkspaceId || !ref) {
          appendWorkspaceError(getWorkspaceErrorBody());
          return;
        }

        try {
          const { originalJson, submittedJson } = await getGeneratedProblemJsons(ref);
          await applyGeneratedProblemJson({
            ref,
            originalJson,
            json: submittedJson,
            messageId,
          });
        } catch {
          setSelectedActionsByMessage((current) => {
            const { [String(messageId)]: _selected, ...rest } = current;
            return rest;
          });
          setThreadItems((current) =>
            current.map((item): ThreadItem =>
              item.id === messageId
                ? {
                    ...(item as ResponseThreadItem),
                    body: routingAiChat.generatedProblemLoadError,
                    variant: RESPONSE_VARIANTS.WARNING,
                  }
                : item,
            ),
          );
        }
        return;
      }

      if (action.key === 'editGeneratedProblem') {
        const ref = action.artifactRef;
        if (!user.aiChatWorkspaceId || !ref) {
          appendWorkspaceError(getWorkspaceErrorBody());
          return;
        }
        if (isMobileDevice()) {
          appendWorkspaceError(routingAiChat.generatedProblemDesktopOnly);
          return;
        }
        try {
          await openGeneratedProblemJson({ ref, messageId, mode: 'edit' });
        } catch {
          setThreadItems((current) =>
            current.map((item): ThreadItem =>
              item.id === messageId
                ? {
                    ...(item as ResponseThreadItem),
                    body: routingAiChat.generatedProblemLoadError,
                    variant: RESPONSE_VARIANTS.WARNING,
                  }
                : item,
            ),
          );
        }
        return;
      }

      if (action.key === 'viewGeneratedProblem') {
        const ref = action.artifactRef;
        if (!user.aiChatWorkspaceId || !ref) {
          appendWorkspaceError(getWorkspaceErrorBody());
          return;
        }
        if (isMobileDevice()) {
          appendWorkspaceError(routingAiChat.generatedProblemDesktopOnly);
          return;
        }
        try {
          await openGeneratedProblemJson({ ref, messageId, mode: 'view' });
        } catch {
          setThreadItems((current) =>
            current.map((item): ThreadItem =>
              item.id === messageId
                ? {
                    ...(item as ResponseThreadItem),
                    body: routingAiChat.generatedProblemLoadError,
                    variant: RESPONSE_VARIANTS.WARNING,
                  }
                : item,
            ),
          );
        }
        return;
      }

      const promptKey = action.key as PromptKey;
      setSelectedActionsByMessage((current) => ({
        ...current,
        [String(messageId)]: promptKey,
      }));
      appendFlow(action.key, action.label);
    },
    [
      appendFlow,
      appendWorkspaceError,
      cookies,
      dispatch,
      error,
      getWorkspaceErrorBody,
      getGeneratedProblemJsons,
      applyGeneratedProblemJson,
      map.depotMarkerTrans,
      map.returnLocationMarkerTrans,
      oAuth,
      routingAiChat.generatedProblemLoadError,
      solution.show,
      openGeneratedProblemJson,
      user,
    ],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const value = draft.trim();
      if (!value || isStreaming || isWorkspaceConnecting) return;

      if (!user.aiChatWorkspaceId) {
        appendWorkspaceError(getWorkspaceErrorBody());
        return;
      }

      setDraft('');

      const promptId = nextMessageId.current++;
      const streamingCardId = nextMessageId.current++;
      streamingCardIdRef.current = streamingCardId;
      streamingPendingReset.current = false;
      nextTraceIdRef.current = 0;

      setThreadItems((current) => [
        ...current,
        { id: promptId, type: THREAD_ITEM_TYPES.PROMPT, text: value },
        {
          id: streamingCardId,
          type: THREAD_ITEM_TYPES.RESPONSE,
          variant: RESPONSE_VARIANTS.ROUTING,
          label: routingAiChat.cardLabels.routingAi,
          body: '',
          actions: [],
          streaming: true,
          traceSteps: [],
        },
      ]);

      setIsStreaming(true);
      const controller = new AbortController();
      abortControllerRef.current = controller;
      let readyCanvasVersion: number | null = null;
      let problemBuilt = false;

      const updateStreamingCard = (updates: Partial<ResponseThreadItem>) => {
        setThreadItems((current) =>
          current.map(
            (item): ThreadItem =>
              item.id === streamingCardId
                ? ({ ...(item as ResponseThreadItem), ...updates } as ResponseThreadItem)
                : item,
          ),
        );
      };

      const pushTraceStep = (step: Omit<TraceStep, 'id'>) => {
        const traceStep: TraceStep = { ...step, id: nextTraceIdRef.current++ };
        setThreadItems((current) =>
          current.map((item): ThreadItem => {
            if (item.id !== streamingCardId) return item;
            const responseItem = item as ResponseThreadItem;
            return {
              ...responseItem,
              traceSteps: [...(responseItem.traceSteps ?? []), traceStep],
            };
          }),
        );
      };

      try {
        await streamChatMessage({
          message: value,
          workspaceId: user.aiChatWorkspaceId,
          signal: controller.signal,
          onEvent: ({ type, payload }: { type: string; payload: any }) => {
            const agentPath: string[] = payload?.data?.agent_path ?? [];
            const isSubAgent = agentPath.length > 1;
            const tier: string = payload?.tier ?? '';

            if (type === SSE_EVENT_TYPES.MESSAGE_CHUNK) {
              if (isSubAgent) return;
              const delta: string = payload?.data?.text_delta ?? '';
              setThreadItems((current) =>
                current.map((item): ThreadItem => {
                  if (item.id !== streamingCardId) return item;
                  const responseItem = item as ResponseThreadItem;
                  const newBody = streamingPendingReset.current ? delta : responseItem.body + delta;
                  streamingPendingReset.current = false;
                  return { ...responseItem, body: newBody };
                }),
              );
            } else if (type === SSE_EVENT_TYPES.MESSAGE_COMPLETE) {
              const fullText: string = payload?.data?.full_text ?? '';
              if (isSubAgent) {
                const label = fullText.length > 60 ? `${fullText.slice(0, 60)}…` : fullText;
                const detail = fullText.length > 60 ? fullText : undefined;
                pushTraceStep({ kind: 'thought', label, detail });
              } else {
                streamingPendingReset.current = true;
                updateStreamingCard({ body: fullText });
              }
            } else if (type === SSE_EVENT_TYPES.SUBAGENT_INVOKED) {
              const agentName: string = payload?.data?.subagent_name ?? '';
              pushTraceStep({ kind: 'action', label: `Dispatching ${agentName}` });
            } else if (type === SSE_EVENT_TYPES.TOOL_CALLED) {
              const toolName: string = payload?.data?.tool_name ?? 'tool';
              pushTraceStep({ kind: 'tool_call', label: `${toolName}()` });
            } else if (
              type === SSE_EVENT_TYPES.UPDATE_CANVAS_COMPLETED &&
              payload?.data?.status === 'success' &&
              payload?.data?.verb !== 'read' &&
              payload?.data?.readiness === 'ready' &&
              Number.isInteger(payload?.data?.canvas_version)
            ) {
              readyCanvasVersion = payload.data.canvas_version;
            } else if (type === SSE_EVENT_TYPES.TASK_CREATED) {
              startGeocodingTask(payload?.data ?? {}, streamingCardId);
            } else if (
              type === SSE_EVENT_TYPES.TASK_PROGRESS ||
              type === SSE_EVENT_TYPES.TASK_COMPLETED ||
              type === SSE_EVENT_TYPES.TASK_FAILED
            ) {
              const data = payload?.data ?? {};
              if (data.task_id) {
                updateGeocodingTaskTrace({
                  taskId: data.task_id,
                  mode: data.mode ?? 'batch',
                  state: data.state ?? 'running',
                  requested: data.counts?.requested ?? data.requested ?? 0,
                  distinct: data.counts?.distinct ?? data.distinct ?? 0,
                  resolved: data.counts?.resolved ?? data.resolved ?? 0,
                  failed: data.counts?.failed ?? data.failed ?? 0,
                  pending: data.counts?.pending ?? data.pending ?? 0,
                  error: data.error,
                });
              }
            } else if (
              type === SSE_EVENT_TYPES.BUILD_SCHEMA_COMPLETED &&
              payload?.data?.status === 'success' &&
              payload?.data?.output_artifact === 'problem' &&
              Number.isInteger(payload?.data?.output_version)
            ) {
              problemBuilt = true;
              setSelectedActionsByMessage((current) => {
                const { [String(streamingCardId)]: _selected, ...rest } = current;
                return rest;
              });
              updateStreamingCard({
                actions: [
                  {
                    key: 'useGeneratedProblem',
                    label: routingAiChat.cardActions.useGeneratedProblem,
                    artifactRef: {
                      artifact: 'problem',
                      version: payload.data.output_version,
                    },
                  },
                  {
                    key: 'editGeneratedProblem',
                    label: routingAiChat.cardActions.editGeneratedProblem,
                    secondary: true,
                    artifactRef: {
                      artifact: 'problem',
                      version: payload.data.output_version,
                    },
                  },
                ],
                problemArtifactRef: {
                  artifact: 'problem',
                  version: payload.data.output_version,
                },
              });
            } else if (
              type === SSE_EVENT_TYPES.JSON_TOOL_COMPLETED &&
              payload?.data?.tool_name === 'json_mutate' &&
              payload?.data?.artifact === 'problem' &&
              Number.isInteger(payload?.data?.new_version) &&
              !payload?.data?.error
            ) {
              const ref = { artifact: 'problem', version: payload.data.new_version };
              setSelectedActionsByMessage((current) => {
                const { [String(streamingCardId)]: _selected, ...rest } = current;
                return rest;
              });
              updateStreamingCard({
                actions: [
                  {
                    key: 'useGeneratedProblem',
                    label: routingAiChat.cardActions.useGeneratedProblem,
                    artifactRef: ref,
                  },
                  {
                    key: 'editGeneratedProblem',
                    label: routingAiChat.cardActions.editGeneratedProblem,
                    secondary: true,
                    artifactRef: ref,
                  },
                ],
                problemArtifactRef: ref,
              });
            } else if (tier === 'operational' && type.endsWith('.completed')) {
              const canvasSummary: string = payload?.data?.summary ?? '';
              pushTraceStep({ kind: 'observation', label: canvasSummary });
            } else if (
              type === SSE_EVENT_TYPES.ERROR_SYSTEM ||
              type === SSE_EVENT_TYPES.CAP_FIRED
            ) {
              updateStreamingCard({
                variant: RESPONSE_VARIANTS.WARNING,
                label: routingAiChat.cardLabels.headsUp,
                body:
                  type === SSE_EVENT_TYPES.CAP_FIRED
                    ? routingAiChat.chatCapError
                    : routingAiChat.chatStreamError,
                streaming: false,
              });
              setIsStreaming(false);
            }
          },
        });
        if (readyCanvasVersion !== null && !problemBuilt) {
          await resumeAfterCanvasUpdate(readyCanvasVersion);
        }
      } catch (streamError) {
        if ((streamError as Error).name !== 'AbortError') {
          updateStreamingCard({
            variant: RESPONSE_VARIANTS.WARNING,
            label: routingAiChat.cardLabels.headsUp,
            body: routingAiChat.chatStreamError,
            streaming: false,
          });
        }
      } finally {
        streamingCardIdRef.current = null;
        abortControllerRef.current = null;
        setIsStreaming(false);
        setThreadItems((current) =>
          current.map((item): ThreadItem => {
            if (item.id !== streamingCardId) return item;
            const responseItem = item as ResponseThreadItem;
            if (responseItem.variant === RESPONSE_VARIANTS.WARNING) {
              return { ...responseItem, streaming: false };
            }
            return {
              ...responseItem,
              streaming: false,
              variant: RESPONSE_VARIANTS.READY,
              label: routingAiChat.cardLabels.ready,
            };
          }),
        );
      }
    },
    [
      appendWorkspaceError,
      draft,
      getWorkspaceErrorBody,
      isStreaming,
      isWorkspaceConnecting,
      routingAiChat,
      resumeAfterCanvasUpdate,
      startGeocodingTask,
      user.aiChatWorkspaceId,
    ],
  );

  const processCsvFile = useCallback(
    (file: File) => {
      const appendWarning = (body: string) => {
        setThreadItems((current) => [
          ...current,
          {
            id: nextMessageId.current++,
            type: THREAD_ITEM_TYPES.RESPONSE,
            variant: RESPONSE_VARIANTS.WARNING,
            label: routingAiChat.cardLabels.headsUp,
            body,
            actions: [],
          },
        ]);
      };

      if (isWorkspaceConnecting || !user.aiChatWorkspaceId) {
        appendWorkspaceError(getWorkspaceErrorBody());
        return;
      }

      if (!isAcceptedFileType(file)) {
        appendWarning(routingAiChat.uploadCsv.invalidFile);
        return;
      }

      if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
        appendWarning(routingAiChat.uploadCsv.fileTooLarge);
        return;
      }

      const reader = new FileReader();
      reader.onerror = () => {
        appendWarning(routingAiChat.uploadCsv.readError);
      };
      reader.onload = async (e) => {
        const csvText = (e.target?.result as string) || '';
        const promptId = nextMessageId.current++;
        const loadingId = nextMessageId.current++;

        setThreadItems((current) => [
          ...current,
          {
            id: promptId,
            type: THREAD_ITEM_TYPES.PROMPT,
            text: getUploadPromptText(file),
          },
          {
            id: loadingId,
            type: THREAD_ITEM_TYPES.RESPONSE,
            variant: RESPONSE_VARIANTS.ROUTING,
            label: routingAiChat.cardLabels.routingAi,
            body: routingAiChat.uploadCsv.uploading,
            actions: [],
            progress: 0,
          },
        ]);

        try {
          const response = await uploadCsvToAiWorkspace(
            csvText,
            user.aiChatWorkspaceId,
            (progressEvent: { loaded: number; total?: number }) => {
              if (!progressEvent.total) return;

              const nextProgress = Math.min(
                100,
                Math.round((progressEvent.loaded / progressEvent.total) * 100),
              );

              setThreadItems((current) =>
                current.map((item) =>
                  item.id === loadingId
                    ? {
                        ...item,
                        body: `${routingAiChat.uploadCsv.uploading} ${nextProgress}%`,
                        progress: nextProgress,
                      }
                    : item,
                ),
              );
            },
          );
          const artifactInfo: ArtifactInfo | undefined = (response.data as ArtifactInfoDataResponse)
            ?.data;
          const successBody = artifactInfo?.ref
            ? sprintf(routingAiChat.uploadCsv.successTemplate, {
                artifact: artifactInfo.ref.artifact,
                version: artifactInfo.ref.version,
              })
            : routingAiChat.uploadCsv.success;
          setThreadItems((current) =>
            current.map((item) =>
              item.id === loadingId
                ? {
                    ...item,
                    variant: RESPONSE_VARIANTS.READY,
                    label: routingAiChat.cardLabels.ready,
                    body: successBody,
                    actions: [],
                    progress: undefined,
                  }
                : item,
            ),
          );
        } catch (uploadError) {
          const status = axios.isAxiosError(uploadError) ? uploadError.response?.status : undefined;
          setThreadItems((current) =>
            current.map((item) =>
              item.id === loadingId
                ? {
                    ...item,
                    variant: RESPONSE_VARIANTS.WARNING,
                    label: routingAiChat.cardLabels.headsUp,
                    body: getUploadErrorBody(status, routingAiChat),
                    actions: [],
                    progress: undefined,
                  }
                : item,
            ),
          );
        }
      };
      reader.readAsText(file);
    },
    [
      appendWorkspaceError,
      getUploadPromptText,
      getWorkspaceErrorBody,
      isWorkspaceConnecting,
      routingAiChat,
      user.aiChatWorkspaceId,
    ],
  );

  const handleUploadClick = useCallback(() => {
    if (isWorkspaceConnecting || !user.aiChatWorkspaceId) {
      appendWorkspaceError(getWorkspaceErrorBody());
      return;
    }

    fileInputRef.current?.click();
  }, [appendWorkspaceError, getWorkspaceErrorBody, isWorkspaceConnecting, user.aiChatWorkspaceId]);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) processCsvFile(file);
      event.target.value = '';
    },
    [processCsvFile],
  );

  const handleDragEnter = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDraggingOver(true);
  }, []);

  const handleDragOver = useCallback((event: DragEvent<HTMLElement>) => {
    event.preventDefault();
  }, []);

  const handleDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (panelRef.current && !panelRef.current.contains(event.relatedTarget as Node)) {
      setIsDraggingOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      event.preventDefault();
      setIsDraggingOver(false);
      const file = event.dataTransfer.files?.[0];
      if (file) processCsvFile(file);
    },
    [processCsvFile],
  );

  useEffect(() => {
    const isOpen = user.openPanel === PANELS.ROUTING_AI_CHAT;
    setShowPanel(isOpen);
    if (isOpen && !user.aiChatWorkspaceId) {
      initWorkspace();
    }
  }, [initWorkspace, user.aiChatWorkspaceId, user.openPanel]);

  useEffect(() => stopGeocodingPolling, [stopGeocodingPolling]);

  useEffect(() => {
    resetConversation();
  }, [summary.key]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [showPanel, threadItems]);

  return {
    showPanel,
    draft,
    setDraft,
    isDraggingOver,
    isStreaming,
    isWorkspaceConnecting,
    isWorkspaceReady: Boolean(user.aiChatWorkspaceId),
    threadItems,
    selectedActionsByMessage,
    welcomeCard,
    bottomRef,
    panelRef,
    fileInputRef,
    handleClose,
    handleRestart,
    handleCardAction,
    handleSubmit,
    handleUploadClick,
    handleFileChange,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    generatedProblemJson,
    generatedProblemOriginalJson,
    generatedProblemArtifactRef,
    generatedProblemValidationError,
    generatedProblemMode,
    generatedProblemCopied,
    handleCloseGeneratedProblemJson,
    handleCopyGeneratedProblemJson,
    handleGeneratedProblemJsonChange,
    handleApplyGeneratedProblemJson,
  };
};

export default useRoutingAIChat;
